// Tracks whether the app has handed control off to a native UI (camera /
// photo library / file picker). While active, the AppShell guard suppresses
// onboarding redirects so a brief focus/visibility blip during the handoff
// can't bounce the user out of an in-progress add-item flow.

const SS_KEY = "caileve_native_handoff";
const GRACE_MS = 2000;

let active = 0;
let releasedAt = 0;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      // ignore listener errors
    }
  });
}

function writeSession(on: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (on) window.sessionStorage.setItem(SS_KEY, "1");
    else window.sessionStorage.removeItem(SS_KEY);
  } catch {
    // ignore storage errors
  }
}

export function beginNativeHandoff() {
  active += 1;
  writeSession(true);
  notify();
}

export function endNativeHandoff() {
  if (active > 0) active -= 1;
  releasedAt = Date.now();
  if (active === 0) writeSession(false);
  notify();
}

export function isNativeHandoffActive(): boolean {
  if (active > 0) return true;
  if (releasedAt && Date.now() - releasedAt < GRACE_MS) return true;
  return false;
}

export function subscribeNativeHandoff(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// Called once on cold boot. If a handoff flag is still set in sessionStorage
// (indicating a previous session was force-closed mid-handoff), clear it so
// the app doesn't get stuck in a suppressed state.
export function consumeStaleNativeHandoff(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const had = window.sessionStorage.getItem(SS_KEY) === "1";
    if (had) window.sessionStorage.removeItem(SS_KEY);
    return had;
  } catch {
    return false;
  }
}